import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { EditionCounter, type Edition } from '../../components/EditionCounter';
import { ObjectHierarchicalPicker } from '../../components/ObjectHierarchicalPicker';
import { GettyFavoritesStrip } from '../../components/edit-object/GettyFavoritesStrip';
import { GettyHierarchyBreadcrumb } from '../../components/edit-object/GettyHierarchyBreadcrumb';
import { ObjectMetadataValue, HIERARCHY_ROOT_IDS } from '../../types/ObjectHierarchicalMetadataValue';
import { formatControlledVocabulary, formatSingleControlledVocabulary } from '../../utils/catalogFormatters';
import { en } from '../../translations/en';
import { useCatalogDescriptions } from '../../hooks/useCatalogDescriptions';
import { useCatalog } from '../../hooks/useCatalog';
import type { DescriptionData as SaveDescriptionData } from '../../contexts/CatalogContext';
import { useUpload } from '../../hooks/useUpload';
import { useGettyVocabulary } from '../../hooks/useGettyVocabulary';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useRegisterSaveNow } from '../../hooks/useRegisterSaveNow';
import { logger } from '../../utils/logger';

const summarizeSingle = (v: ObjectMetadataValue | null | undefined): string | null =>
  formatSingleControlledVocabulary(v);

const summarizeMulti = (vs: ObjectMetadataValue[] | null | undefined): string | null => {
  if (!vs || vs.length === 0) return null;
  return formatControlledVocabulary(vs);
};

/** Compile-time-checked accordion ids inside MediaTab. */
export type MediaSectionId =
  | 'media-description'
  | 'media-work-type'
  | 'media-techniques'
  | 'media-materials'
  | 'media-substrate'
  | 'media-additional-comments'
  | 'media-editions';

interface MediaTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * Optional whitelist of accordion ids to render. When undefined, every
   * section renders (preserves the classic /upload-object behavior). The
   * /edit-object redesign passes this to split MediaTab across two new
   * steps: free-text media fields (Describe step) vs. Getty AAT pickers
   * (Getty step).
   */
  visibleSections?: ReadonlySet<MediaSectionId>;
  /**
   * When true, suppresses the in-work-type multiples checkbox + EditionCounter
   * UI. The /edit-object redesign surfaces multiples/editions in a dedicated
   * Artwork Details section, so the Getty Vocab step (which only shows the
   * Work Type picker) shouldn't duplicate that data. Save behavior is
   * unaffected — `hasMultiples` and `editions` continue to load/save through
   * this tab; only the UI is hidden.
   */
  hideMultiplesInWorkType?: boolean;
}

/** Fields owned by DescribeTab that we pass through on save */
interface DescribeFields {
  creators: Array<{ name: string; isCurrentUser: boolean; notes?: string }>;
  title: string;
  titleLocation: string[];
  alternativeTitles: string[];
  titleAsWritten: string;
  signatures: Array<{ type: string[]; location: string[]; display: string }>;
  creationDate: { type: string; value: string; location: string[]; display: string };
  stepNotes: Record<string, string[]>;
}

export const MediaTab = ({ catalogItemId, ownerId, visibleSections, hideMultiplesInWorkType }: MediaTabProps) => {
  const [hasError, setHasError] = useState(false);
  const showSection = (id: MediaSectionId) =>
    !visibleSections || visibleSections.has(id);

  const { currentItem } = useCatalog();
  const { catalogId } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const { loadDescription, updateDescription } = useCatalogDescriptions(effectiveCatalogId, ownerId);
  const { loading: gettyLoading } = useGettyVocabulary();
  const { batchUpdateTab } = useWizardCompletion();
  const { setStatus, setLastSaveTime } = useAutosaveStatus();

  // State management
  // isLoadingInitialData starts true so the autosave guard is engaged from
  // first render — protects against debounce/unmount-flush firing before the
  // load useEffect has even run. Cleared in the load effect's .finally().
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  // Ref mirrors the state so the unmount cleanup can read the current value
  // (state would be stale in cleanup closures).
  const isLoadingInitialDataRef = useRef(true);
  useEffect(() => { isLoadingInitialDataRef.current = isLoadingInitialData; }, [isLoadingInitialData]);
  const loadedCatalogIdRef = useRef<string | null>(null);

  // Form state — fields owned by this tab
  const [mediaDescription, setMediaDescription] = useState('');
  const [mediaAdditionalComments, setMediaAdditionalComments] = useState('');
  const [workType, setWorkType] = useState<ObjectMetadataValue | null>(null);
  const [hasMultiples, setHasMultiples] = useState(false);
  const [editions, setEditions] = useState<Array<{ type: string; count: number; total: number }>>([]);
  const [techniques, setTechniques] = useState<ObjectMetadataValue[]>([]);
  const [materials, setMaterials] = useState<ObjectMetadataValue[]>([]);
  const [substrate, setSubstrate] = useState<ObjectMetadataValue[]>([]);

  // Passthrough ref for DescribeTab's fields — populated on load, merged on save.
  // Trade-off: Each tab loads the full description record and stores the other tab's fields
  // in a ref. Refs are refreshed on each tab switch via loadDescription(). A narrow race exists
  // if DescribeTab's debounced save hasn't completed when this tab's loadDescription fires,
  // but handleBlur (immediate save) on tab switch significantly narrows this window.
  // Long-term fix: partial-update API endpoint so each tab only saves its own fields.
  const describeFieldsRef = useRef<DescribeFields>({
    creators: [],
    title: '',
    titleLocation: [],
    alternativeTitles: [],
    titleAsWritten: '',
    signatures: [],
    creationDate: { type: '', value: '', location: [], display: '' },
    stepNotes: {},
  });

  // Smart auto-save with multiple triggers
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveDataRef = useRef<() => Promise<void> | void>(() => {});
  const lastSaveRef = useRef<string>('');
  // Let the wizard "Finished" button flush this tab's pending save.
  useRegisterSaveNow(saveDataRef, saveTimeoutRef);

  // Completion tracking
  const updateCompletionTracking = useCallback(() => {
    batchUpdateTab('media', {
      0: workType !== null,
      1: techniques.length > 0,
      2: materials.length > 0,
      3: substrate.length > 0
    });
  }, [batchUpdateTab, workType, techniques, materials, substrate]);

  const saveData = useCallback(async () => {
    // Guard: never save if initial load failed — prevents overwriting real data with empty defaults
    if (!effectiveCatalogId || isLoadingInitialData || hasError) {
      return;
    }

    // Merge own fields with passthrough ref to rebuild full record
    const descriptionData: SaveDescriptionData = {
      ...describeFieldsRef.current,
      mediaDescription,
      mediaAdditionalComments,
      workType,
      hasMultiples,
      editions,
      techniques,
      materials,
      substrate,
    };

    const currentDataString = JSON.stringify(descriptionData);
    if (currentDataString === lastSaveRef.current) {
      return;
    }

    setStatus('saving');
    try {
      await updateDescription(descriptionData);
      lastSaveRef.current = currentDataString;
      updateCompletionTracking();
      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (error) {
      setStatus('error');
      logger.error('MediaTab: Failed to auto-save', error);
    }
  }, [effectiveCatalogId, isLoadingInitialData, hasError, updateDescription, mediaDescription, mediaAdditionalComments, workType, hasMultiples, editions, techniques, materials, substrate, updateCompletionTracking, setStatus, setLastSaveTime]);

  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveDataRef.current?.();
    }, 2000);
  }, []);

  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveDataRef.current?.();
  }, []);

  // Load existing description data on mount
  useEffect(() => {
    if (effectiveCatalogId && loadedCatalogIdRef.current !== effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      // Set guard BEFORE the fetch — closes the race where debounce/unmount-flush
      // could otherwise fire with empty defaults and wipe the row.
      setIsLoadingInitialData(true);
      loadDescription().then((data) => {
        const typedData = data as Record<string, unknown> | null;
        if (typedData) {
          // Populate passthrough ref with DescribeTab's fields
          // Migrate legacy string locations to arrays for passthrough fields
          const rawTitleLoc = typedData.titleLocation;
          const titleLoc = Array.isArray(rawTitleLoc)
            ? rawTitleLoc as string[]
            : (typeof rawTitleLoc === 'string' && rawTitleLoc ? [rawTitleLoc] : []);
          const rawCreationDate = typedData.creationDate as DescribeFields['creationDate'] | undefined;
          const creationDateLoc = rawCreationDate
            ? (Array.isArray(rawCreationDate.location)
                ? rawCreationDate.location
                : (typeof rawCreationDate.location === 'string' && rawCreationDate.location
                    ? [rawCreationDate.location]
                    : []))
            : [];
          const rawSigs = (typedData.signatures as DescribeFields['signatures']) || [];
          const migratedSigs = rawSigs.map(sig => ({
            ...sig,
            location: Array.isArray(sig.location)
              ? sig.location
              : (typeof sig.location === 'string' && sig.location ? [sig.location] : []),
          }));
          describeFieldsRef.current = {
            creators: (typedData.creators as DescribeFields['creators']) || [],
            title: (typedData.title as string) || '',
            titleLocation: titleLoc,
            alternativeTitles: (typedData.alternativeTitles as string[]) || [],
            titleAsWritten: (typedData.titleAsWritten as string) || '',
            signatures: migratedSigs,
            creationDate: rawCreationDate
              ? { ...rawCreationDate, location: creationDateLoc }
              : { type: '', value: '', location: [], display: '' },
            stepNotes: (typedData.stepNotes as Record<string, string[]>) || {},
          };

          // Set own fields
          setMediaDescription((typedData.mediaDescription as string) || '');
          setMediaAdditionalComments((typedData.mediaAdditionalComments as string) || '');
          setWorkType((typedData.workType as ObjectMetadataValue) || null);
          setHasMultiples((typedData.hasMultiples as boolean) || false);
          setEditions((typedData.editions as Edition[]) || []);
          setTechniques((typedData.techniques as ObjectMetadataValue[]) || []);
          setMaterials((typedData.materials as ObjectMetadataValue[]) || []);
          setSubstrate((typedData.substrate as ObjectMetadataValue[]) || []);

          // Initialize lastSaveRef from full merged record to prevent spurious first-save.
          // Key order MUST match descriptionData in saveData (JSON.stringify is order-sensitive).
          const fullRecord: SaveDescriptionData = {
            ...describeFieldsRef.current,
            mediaDescription: (typedData.mediaDescription as string) || '',
            mediaAdditionalComments: (typedData.mediaAdditionalComments as string) || '',
            workType: (typedData.workType as ObjectMetadataValue) || null,
            hasMultiples: (typedData.hasMultiples as boolean) || false,
            editions: (typedData.editions as Edition[]) || [],
            techniques: (typedData.techniques as ObjectMetadataValue[]) || [],
            materials: (typedData.materials as ObjectMetadataValue[]) || [],
            substrate: (typedData.substrate as ObjectMetadataValue[]) || [],
          };
          lastSaveRef.current = JSON.stringify(fullRecord);

          // Update completion tracking from loaded data directly
          batchUpdateTab('media', {
            0: typedData.workType !== null && typedData.workType !== undefined,
            1: ((typedData.techniques as unknown[]) || []).length > 0,
            2: ((typedData.materials as unknown[]) || []).length > 0,
            3: ((typedData.substrate as unknown[]) || []).length > 0
          });
        }
        setIsInitialized(true);
        setIsLoadingInitialData(false);
      }).catch((error) => {
        logger.error('MediaTab: Failed to load description data', error);
        setHasError(true);
        setIsInitialized(true);
        setIsLoadingInitialData(false);
      });
    } else if (!effectiveCatalogId) {
      // Fresh-create flow: no id to load. Clear the guard so saves can proceed
      // once an id arrives (the load effect re-runs on id change).
      setIsLoadingInitialData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId]);

  // Completion tracking when form fields change
  useEffect(() => {
    if (isInitialized && !isLoadingInitialData) {
      updateCompletionTracking();
    }
  }, [isInitialized, isLoadingInitialData, workType, techniques, materials, substrate, updateCompletionTracking]);

  // Cleanup timeout on unmount + flush save
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Never flush during the initial load window — would write empty defaults
      // and wipe the row (regression for DA-55L.2499).
      if (isLoadingInitialDataRef.current) return;
      try {
        const maybePromise = saveDataRef.current?.();
        if (maybePromise && typeof (maybePromise as { then?: unknown }).then === 'function') {
          (maybePromise as Promise<void>).catch((err) =>
            logger.error('MediaTab: Flush save failed on unmount', err)
          );
        }
      } catch (err) {
        logger.error('MediaTab: Flush save failed on unmount (sync)', err);
      }
    };
  }, []);

  useEffect(() => {
    saveDataRef.current = saveData;
  }, [saveData]);

  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Something went wrong loading the media form.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please try refreshing the page or contact support if the issue persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />

      {/* Media Description - Free Text */}
      {showSection('media-description') && (
      <WizardAccordion
        id="media-description"
        title="Media Description"
        subtitle="Describe the media used in simple terms. This appears on the public view instead of the structured fields below."
        defaultExpanded={true}
        summary={
          mediaDescription.trim()
            ? mediaDescription.trim().split('\n')[0].slice(0, 80) + (mediaDescription.length > 80 ? '…' : '')
            : 'No media description'
        }
        privacyFieldPath="describe.mediaDescription"
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="e.g., Oil on canvas, Graphite pencil on paper, Bronze cast"
          variant="outlined"
          value={mediaDescription}
          onChange={(e) => {
            setMediaDescription(e.target.value);
            debouncedSave();
          }}
          onBlur={handleBlur}
          helperText="If provided, this text appears on the public view instead of the structured technique and material fields below."
        />
      </WizardAccordion>
      )}

      {/* Work Type */}
      {showSection('media-work-type') && (
      <WizardAccordion
        id="media-work-type"
        title={en.wizard.describe.steps.workType.workType.title}
        subtitle={en.wizard.describe.steps.workType.workType.subtitle}
        defaultExpanded={true}
        privacyFieldPath="describe.workType"
        headerSummary={summarizeSingle(workType) || undefined}
      >
        <GettyFavoritesStrip
          vocabularyType="work_type"
          currentValue={workType || { textValue: '', isFreetext: true }}
          multiple={false}
          onSelect={(value) => {
            const singleValue = Array.isArray(value) ? value[0] : value;
            setWorkType(singleValue);
            debouncedSave();
          }}
        />
        <ObjectHierarchicalPicker
          hierarchyRootId={HIERARCHY_ROOT_IDS.workTypes}
          label={gettyLoading ? "Loading work types..." : en.wizard.describe.steps.workType.workType.label}
          value={workType || { textValue: '', isFreetext: true }}
          onChange={(value) => {
            const singleValue = Array.isArray(value) ? value[0] : value;
            setWorkType(singleValue);
            debouncedSave();
          }}
          multiple={false}
          disabled={gettyLoading}
        />
        <GettyHierarchyBreadcrumb value={workType} />
        {!hideMultiplesInWorkType && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasMultiples}
                  onChange={(e) => {
                    setHasMultiples(e.target.checked);
                    debouncedSave();
                  }}
                />
              }
              label={en.wizard.describe.steps.workType.workType.multiplesCheckbox}
              sx={{ mt: 2 }}
            />
            {hasMultiples && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {en.wizard.describe.steps.workType.editions.helperText}
                </Typography>
                <EditionCounter
                  label={en.wizard.describe.steps.workType.editions.label}
                  addButtonText={en.wizard.describe.steps.workType.editions.addButton}
                  options={en.wizard.describe.steps.workType.editions.options}
                  freeTextSentinel={en.wizard.describe.steps.workType.editions.freeTextSentinel}
                  value={editions}
                  onChange={(newEditions) => {
                    setEditions(newEditions);
                    debouncedSave();
                  }}
                />
              </Box>
            )}
          </>
        )}
      </WizardAccordion>
      )}

      {/* Techniques */}
      {showSection('media-techniques') && (
      <WizardAccordion
        id="media-techniques"
        title={en.wizard.describe.steps.techniques.title}
        subtitle={en.wizard.describe.steps.techniques.subtitle}
        defaultExpanded={true}
        privacyFieldPath="describe.techniques"
        headerSummary={summarizeMulti(techniques) || undefined}
      >
        <GettyFavoritesStrip
          vocabularyType="techniques"
          currentValue={techniques}
          multiple={true}
          onSelect={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setTechniques(arrayValues);
            debouncedSave();
          }}
        />
        <ObjectHierarchicalPicker
          hierarchyRootId={HIERARCHY_ROOT_IDS.techniques}
          label={gettyLoading ? "Loading techniques..." : en.wizard.describe.steps.techniques.label}
          value={techniques}
          onChange={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setTechniques(arrayValues);
            debouncedSave();
          }}
          multiple={true}
          disabled={gettyLoading}
        />
        <GettyHierarchyBreadcrumb value={techniques} />
      </WizardAccordion>
      )}

      {/* Materials */}
      {showSection('media-materials') && (
      <WizardAccordion
        id="media-materials"
        title={en.wizard.describe.steps.materials.title}
        subtitle={en.wizard.describe.steps.materials.subtitle}
        defaultExpanded={true}
        privacyFieldPath="describe.materials"
        headerSummary={summarizeMulti(materials) || undefined}
      >
        <GettyFavoritesStrip
          vocabularyType="materials"
          currentValue={materials}
          multiple={true}
          onSelect={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setMaterials(arrayValues);
            debouncedSave();
          }}
        />
        <ObjectHierarchicalPicker
          hierarchyRootId={HIERARCHY_ROOT_IDS.materials}
          label={gettyLoading ? "Loading materials..." : en.wizard.describe.steps.materials.label}
          value={materials}
          onChange={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setMaterials(arrayValues);
            debouncedSave();
          }}
          multiple={true}
          disabled={gettyLoading}
        />
        <GettyHierarchyBreadcrumb value={materials} />
      </WizardAccordion>
      )}

      {/* Substrate */}
      {showSection('media-substrate') && (
      <WizardAccordion
        id="media-substrate"
        title={en.wizard.describe.steps.substrate.title}
        subtitle={en.wizard.describe.steps.substrate.subtitle}
        defaultExpanded={true}
        privacyFieldPath="describe.substrate"
        headerSummary={summarizeMulti(substrate) || undefined}
      >
        <GettyFavoritesStrip
          vocabularyType="substrate"
          currentValue={substrate}
          multiple={true}
          onSelect={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setSubstrate(arrayValues);
            debouncedSave();
          }}
        />
        <ObjectHierarchicalPicker
          hierarchyRootId={HIERARCHY_ROOT_IDS.substrates}
          label={gettyLoading ? "Loading substrates..." : en.wizard.describe.steps.substrate.label}
          value={substrate}
          onChange={(values) => {
            const arrayValues = Array.isArray(values) ? values : [values];
            setSubstrate(arrayValues);
            debouncedSave();
          }}
          multiple={true}
          disabled={gettyLoading}
        />
        <GettyHierarchyBreadcrumb value={substrate} />
      </WizardAccordion>
      )}

      {/* Multiples / Editions — standalone accordion for the edit-object Describe step.
          Surfaces the same hasMultiples + EditionCounter UI that lives inside the
          Work Type accordion so it can render in the Describe page where Work Type
          itself is filtered out. Default-collapsed; summary chip = edition count. */}
      {showSection('media-editions') && (
      <WizardAccordion
        id="media-editions"
        title="Multiples / Editions"
        subtitle="Whether this work is a unique piece or part of an edition."
        defaultExpanded={false}
        summary={
          !hasMultiples
            ? 'Unique work'
            : editions.length === 0
              ? 'Edition (no details yet)'
              : editions.map(e => `${e.type} ${e.count}/${e.total}`).join(', ')
        }
        privacyFieldPath="describe.editions"
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={hasMultiples}
              onChange={(e) => {
                setHasMultiples(e.target.checked);
                debouncedSave();
              }}
            />
          }
          label={en.wizard.describe.steps.workType.workType.multiplesCheckbox}
        />
        {hasMultiples && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {en.wizard.describe.steps.workType.editions.helperText}
            </Typography>
            <EditionCounter
              label={en.wizard.describe.steps.workType.editions.label}
              addButtonText={en.wizard.describe.steps.workType.editions.addButton}
              options={en.wizard.describe.steps.workType.editions.options}
              freeTextSentinel={en.wizard.describe.steps.workType.editions.freeTextSentinel}
              value={editions}
              onChange={(newEditions) => {
                setEditions(newEditions);
                debouncedSave();
              }}
            />
          </Box>
        )}
      </WizardAccordion>
      )}

      {/* Additional Comments — free text (issue #1026) */}
      {showSection('media-additional-comments') && (
      <WizardAccordion
        id="media-additional-comments"
        title={en.wizard.describe.steps.mediaAdditionalComments.title}
        subtitle={en.wizard.describe.steps.mediaAdditionalComments.subtitle}
        defaultExpanded={true}
        privacyFieldPath="describe.mediaAdditionalComments"
      >
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={en.wizard.describe.steps.mediaAdditionalComments.placeholder}
          variant="outlined"
          value={mediaAdditionalComments}
          onChange={(e) => {
            setMediaAdditionalComments(e.target.value);
            debouncedSave();
          }}
          onBlur={handleBlur}
        />
      </WizardAccordion>
      )}

    </Box>
  );
};
